/**
 * Period + fiscal-year endpoints, as measured against the live bos-api.
 *
 * Two things about the shape of this surface, both different from what the
 * client originally assumed and both load-bearing:
 *
 * 1. **Everything is keyed by the integer year, not by a row id.** The payloads
 *    carry uuids; no route does. A period is addressed by year *and* period
 *    number — which is why `AccountingPeriod.id` is the composite
 *    `'{year}:{periodNumber}'` (see `periodsMapping`).
 * 2. **There is no period-list route and no period-status route.** A year's
 *    periods arrive nested on the year itself, and a date's postability is
 *    resolved from that same read.
 *
 * Every mutating call is sent with **no request body**. The close and the
 * rollover accept an optional `{ date }`, deliberately never sent: it is
 * honoured, and sending it moves the posted entry off the date the preview
 * showed. Sending nothing also means there is no payload for a `source` to
 * travel in — `CLOSING` / `ROLLOVER` are server-assigned.
 *
 * Two live paths this client never calls, listed so their absence is a decision
 * rather than an oversight:
 *
 * - `POST /fiscal-years/{year}/lock` and `…/periods/{n}/lock` — **no inverse
 *   exists**. Locking is unrecoverable through the API, so the screen does not
 *   offer it.
 * - `POST /fiscal-years/{year}/generate` — creates a year's twelve periods.
 *   Nothing in this screen creates fiscal years; the selector lists what the
 *   ledger has.
 */
const LEDGER = '/ledger/fiscal-years';

export const PeriodsEndpoints = {
    /** Every fiscal year, each with its periods nested. */
    fiscalYears: () => LEDGER,
    /** One fiscal year — the freshest copy of a year and its periods. */
    fiscalYear: (year: number) => `${LEDGER}/${year}`,
    closePeriod: (year: number, periodNumber: number) =>
        `${LEDGER}/${year}/periods/${periodNumber}/close`,
    reopenPeriod: (year: number, periodNumber: number) =>
        `${LEDGER}/${year}/periods/${periodNumber}/reopen`,
    /**
     * Dry run of the year-end close — a **GET**, and the only period endpoint
     * that takes a query parameter (`locale`, measured to change `accountName`).
     */
    closePreview: (year: number) => `${LEDGER}/${year}/close/preview`,
    close: (year: number) => `${LEDGER}/${year}/close`,
    /**
     * Post the ROLLOVER entry.
     *
     * **There is no `/rollover/preview`** — it 404s on both GET and POST. That
     * absence is why the rollover action stays behind `LedgerHandoffNotice`:
     * committing it would mean posting a real entry with no dry run to approve.
     */
    rollover: (year: number) => `${LEDGER}/${year}/rollover`,
    /** Reopen by reversing the rollover and closing entries. */
    reopen: (year: number) => `${LEDGER}/${year}/reopen`,
};
